import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Meta from 'components/App/Meta';
import images from 'assets/images';
import { LOCATIONS } from 'constants/index';
import cn from 'utils/classnames';

import styles from './LegalDocumentPage.module.scss';

const renderItemContent = (item) => {
  const hasContent = Array.isArray(item.content) && item.content.length > 0;

  if (!hasContent) {
    return (
      <p className={styles.Paragraph}>
        {item.title}
      </p>
    );
  }

  const isNumericTitle = /^\d+(\.\d+)?$/.test(item.title);

  return (
    <>
      {!isNumericTitle && (
        <h3 className={styles.ItemTitle}>
          {item.title}
        </h3>
      )}
      {item.content.map((paragraph) => {
        const text = isNumericTitle && paragraph === item.content[0]
          ? `${item.title}. ${paragraph}`
          : paragraph;

        return (
          <p
            key={paragraph}
            className={styles.Paragraph}
          >
            {text}
          </p>
        );
      })}
    </>
  );
};

function LegalSection({ section, isOpen, onToggle }) {
  const hasBody = section.paragraphs?.length > 0 || section.items?.length > 0;

  return (
    <section className={cn(styles.Section, isOpen && styles.SectionOpen)}>
      <button
        type="button"
        className={styles.SectionHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className={styles.SectionTitle}>{section.title}</span>
        <i
          className={cn(
            'fas',
            isOpen ? 'fa-chevron-up' : 'fa-chevron-down',
            styles.SectionChevron,
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen && hasBody && (
        <div className={styles.SectionBody}>
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph} className={styles.Paragraph}>
              {paragraph}
            </p>
          ))}

          {section.items?.map((item) => (
            <div key={item.id} className={styles.Item}>
              {renderItemContent(item)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

LegalSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    paragraphs: PropTypes.arrayOf(PropTypes.string),
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      content: PropTypes.arrayOf(PropTypes.string),
    })),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function LegalDocumentPage({ document }) {
  const { meta, sections } = document;
  const [openSectionIds, setOpenSectionIds] = useState(() => (
    sections[0] ? { [sections[0].id]: true } : {}
  ));

  const openCount = useMemo(
    () => Object.values(openSectionIds).filter(Boolean).length,
    [openSectionIds],
  );

  const onToggleSection = useCallback((sectionId) => {
    setOpenSectionIds((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const onExpandAll = useCallback(() => {
    const next = {};
    sections.forEach((section) => {
      next[section.id] = true;
    });
    setOpenSectionIds(next);
  }, [sections]);

  const onCollapseAll = useCallback(() => {
    setOpenSectionIds({});
  }, []);

  return (
    <div className={styles.Page}>
      <Meta name={meta.title} />

      <header className={styles.Header}>
        <div className={styles.HeaderInner}>
          <Link to={LOCATIONS.LOGIN} className={styles.LogoLink}>
            <img
              src={images.logoBookeseFull.default}
              alt="Bookese"
              className={styles.Logo}
            />
          </Link>

          <Link to={LOCATIONS.LOGIN} className={styles.BackLink}>
            ← Quay lại đăng nhập
          </Link>
        </div>
      </header>

      <main className={styles.Main}>
        <div className={styles.Hero}>
          <p className={styles.Company}>{meta.company}</p>
          <h1 className={styles.Title}>{meta.title}</h1>
          {meta.subtitle && (
            <p className={styles.Subtitle}>{meta.subtitle}</p>
          )}
          {meta.intro && (
            <p className={styles.Intro}>{meta.intro}</p>
          )}
        </div>

        <div className={styles.Toolbar}>
          <button
            type="button"
            className={styles.ToolbarButton}
            onClick={onExpandAll}
            disabled={openCount === sections.length}
          >
            Mở tất cả
          </button>
          <button
            type="button"
            className={styles.ToolbarButton}
            onClick={onCollapseAll}
            disabled={openCount === 0}
          >
            Thu gọn tất cả
          </button>
        </div>

        <div className={styles.Sections}>
          {sections.map((section) => (
            <LegalSection
              key={section.id}
              section={section}
              isOpen={Boolean(openSectionIds[section.id])}
              onToggle={() => onToggleSection(section.id)}
            />
          ))}
        </div>
      </main>

      <footer className={styles.Footer}>
        <p>
          ©
          {' '}
          {new Date().getFullYear()}
          {' '}
          Bookese
        </p>
      </footer>
    </div>
  );
}

LegalDocumentPage.propTypes = {
  document: PropTypes.shape({
    meta: PropTypes.shape({
      company: PropTypes.string,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      intro: PropTypes.string,
    }).isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      paragraphs: PropTypes.arrayOf(PropTypes.string),
      items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        content: PropTypes.arrayOf(PropTypes.string),
      })),
    })).isRequired,
  }).isRequired,
};

export default LegalDocumentPage;
